import { createContext, PropsWithChildren, useContext } from "react"
import { Employment, EngagementState } from "@oysterhr/schema"
import { disableAgreementFieldsSteps } from "~/app/utils/constants"
import { useContractFieldsQuery } from "@oysterhr/hiring/employment-agreements"

interface ContractFieldsContextStruct {
  contractFields: Array<string>
  personalInfoContractFields: Array<string>
  hasContractFieldsFn: (fields: Array<string>) => boolean
  isContractFieldFn: (field: string) => boolean
  isInDisableChangeStep: () => boolean
}

type ContractFieldsProviderProps = PropsWithChildren<{
  employment: Employment
}>

const ContractFieldsContext = createContext<ContractFieldsContextStruct | null>(null)

export const ContractFieldsProvider = ({ employment, children }: ContractFieldsProviderProps) => {
  const {
    contractFields,
    personalInfoContractFields,
    restrictiveCovenantContractFields,
    hiring,
    employmentState,
    hiringProcess,
  } = useContractFieldsQuery({
    employmentId: employment.id,
    engagementType: employment.engagementType,
  })
  /**
   * Helper function to check if a an Array of field names contains a contract field
   */
  const hasContractFieldsFn = (fields: Array<string>) => {
    return fields.some(
      field =>
        contractFields.includes(field) ||
        personalInfoContractFields.includes(field) ||
        restrictiveCovenantContractFields.includes(field),
    )
  }

  const isContractFieldFn = (fullFieldName: string) => {
    const name = fullFieldName.split(".").slice(-1)[0]
    return (
      contractFields.includes(name) ||
      personalInfoContractFields.includes(name) ||
      restrictiveCovenantContractFields.includes(name)
    )
  }

  const isInDisableChangeStep = () => {
    const { companyProcess, employeeProcess } = hiring ?? {}

    const isEngaged = employmentState?.toUpperCase() === EngagementState.Engaged

    // Condition for the new hiring process
    if (hiringProcess?.agreementsFieldsBlocked && !isEngaged) {
      return true
    }

    if (
      // Employment state is the same as engagement state, but in lowercase
      isEngaged ||
      !companyProcess ||
      !employeeProcess
    )
      return false

    return (
      disableAgreementFieldsSteps.includes(companyProcess?.currentStep?.name) ||
      disableAgreementFieldsSteps.includes(employeeProcess?.currentStep?.name)
    )
  }

  return (
    <ContractFieldsContext.Provider
      value={{
        contractFields,
        personalInfoContractFields,
        hasContractFieldsFn,
        isContractFieldFn,
        isInDisableChangeStep,
      }}
    >
      {children}
    </ContractFieldsContext.Provider>
  )
}

export const useContractFieldsProvider = () => {
  const context = useContext(ContractFieldsContext)

  if (!context) {
    throw new Error("useContractFieldsProvider needs to be used inside a ContractFieldsProvider")
  }

  return context
}
